import React from 'react';
import { Panel } from 'src/modules/shared/components/molecules/panel';
import { PanelHeader } from 'src/modules/shared/components/molecules/panel/panel-header';
import { Text } from 'src/modules/shared/components/atoms/text';
import Loading from 'src/modules/shared/components/atoms/loading';
import { useWorkflowVersion } from 'src/modules/shared/hooks/api/automations/workflows/version';
import { WorkflowCanvas } from '../molecules/workflow-canvas/workflow-canvas';
import { WorkflowDetailsPanel } from '../molecules/workflow-details-panel/index';
// import Tag from 'src/modules/shared/components/atoms/tag';
import { useAutomationsWorkflows } from 'src/modules/shared/hooks/api/automations/workflows/workflows';
import type { WorkflowMetadata } from 'src/api/generated';
import { ErrorState } from 'src/modules/shared/components/atoms/states/error-state';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { useNavigate } from '@tanstack/react-router';
import { Route as ToolsIndexRoute } from 'src/routes/tools.index';
import { Route as WorkflowEditRoute } from 'src/routes/tools/workflows/$workflowId.edit';
import { PiArrowLeft, PiPencilSimpleLine } from 'react-icons/pi';

type WorkflowDetailPageProps = {
  workflowId: string;
};

export const WorkflowDetailPage: React.FC<WorkflowDetailPageProps> = ({
  workflowId,
}) => {
  const navigate = useNavigate();
  const { workflowVersion, loading, error } = useWorkflowVersion(
    workflowId,
    'DRAFT',
    true
  );
  {
    /* TODO: uncomment when implementing workflow versioning */
  }
  // const version = 'DRAFT';
  const [selectedStepId, setSelectedStepId] = React.useState<
    string | undefined
  >();

  // Fetch workflows metadata to display human-friendly name
  const { workflows } = useAutomationsWorkflows(100, true);
  const workflowMeta = React.useMemo(
    () => workflows.find((w: WorkflowMetadata) => w.workflowId === workflowId),
    [workflows, workflowId]
  );

  const definition = workflowVersion?.definition;

  React.useEffect(() => {
    if (!definition) return;
    if (selectedStepId) return;
    const initialId = definition.initialStepId;
    if (initialId) {
      setSelectedStepId(initialId);
    }
  }, [definition, selectedStepId]);

  const formattedUpdatedAt = React.useMemo(() => {
    if (!workflowVersion?.updatedAt) return null;
    try {
      return new Date(workflowVersion.updatedAt).toLocaleString();
    } catch {
      return workflowVersion.updatedAt;
    }
  }, [workflowVersion?.updatedAt]);

  const handleBack = React.useCallback(() => {
    navigate({
      to: ToolsIndexRoute.to,
      search: { phoneVersion: 'v1' },
    });
  }, [navigate]);

  const handleEdit = React.useCallback(() => {
    navigate({
      to: WorkflowEditRoute.to,
      params: { workflowId },
      search: { phoneVersion: 'v1' },
    });
  }, [navigate, workflowId]);

  return (
    <div className="flex flex-col h-full">
      <Panel className="h-full flex flex-col">
        <PanelHeader
          fixed
          className="px-4 py-3 border-b"
          actions={
            <div className="flex items-center gap-2">
              <Button
                variant="neutral"
                appearance="ghost"
                size="md"
                onClick={handleBack}
              >
                <div className="flex items-center gap-2">
                  <PiArrowLeft className="w-4 h-4" />
                  <Text size="small">Volver</Text>
                </div>
              </Button>
              <Button variant="primary" size="md" onClick={handleEdit}>
                <div className="flex items-center gap-2">
                  <PiPencilSimpleLine className="w-4 h-4" />
                  <Text size="small">Editar</Text>
                </div>
              </Button>
            </div>
          }
        >
          <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              <Text
                as="h2"
                size="h2"
                weight="medium"
                className="text-slate-900 break-words"
              >
                {workflowMeta?.name || workflowId}
              </Text>
              {/* TODO: uncomment when implementing workflow versioning */}
              {/* (
                  <Tag
                    content="Draft"
                    size="sm"
                    appearance="soft"
                    variant="warning"
                    textClassName="!text-amber-700"
                  />
                ) */}
            </div>
          </div>
        </PanelHeader>
        <div className="flex-1 overflow-auto p-4">
          <div className="mb-3">
            <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
              {/* TODO: check when implementing i18n */}
              <span>ID: {workflowId}</span>
              {/* TODO: check when implementing i18n */}
              {/* TODO: uncomment when implementing workflow versioning */}
              {/* <span>Versión: {version}</span> */}
              {/* TODO: check when implementing i18n */}
              {formattedUpdatedAt && (
                <span>Actualizado: {formattedUpdatedAt}</span>
              )}
            </div>
          </div>
          {loading ? (
            <div className="h-[70vh] flex items-center justify-center">
              <Loading size="lg" />
            </div>
          ) : error ? (
            <div className="h-[70vh] flex items-center justify-center">
              <ErrorState
                // TODO: check when implementing i18n
                title="Error cargando workflow"
                description={error.message}
                size="sm"
              />
            </div>
          ) : (
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="flex-1 min-w-0">
                <WorkflowCanvas
                  definition={definition}
                  selectedStepId={selectedStepId}
                  onSelectStep={setSelectedStepId}
                />
              </div>
              <WorkflowDetailsPanel
                definition={definition}
                selectedStepId={selectedStepId}
              />
            </div>
          )}
        </div>
      </Panel>
    </div>
  );
};

export default WorkflowDetailPage;
